/**
 * DDL Commands
 *
 * Commands for opening DDL templates (CREATE TABLE, VIEW, etc.) in a query editor.
 * Reuses the existing newObjectScriptService for engine-specific template generation.
 */

import { Table, Eye, Cog, Braces, Database } from 'lucide-react';
import type { CommandDefinition } from '../types/commandTypes';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useContextSelectionStore } from '../../toolbar-context';
import { useConnectionStore } from '../../connections/store/connectionStore';
import {
  handleScriptNewTable,
  handleScriptNewView,
  handleScriptNewProcedure,
  handleScriptNewFunction,
  handleScriptNewDatabase,
  createContextFromDatabaseNode,
} from '../../database-objects/services/newObjectScriptService';
import type { DatabaseEngineId } from '@/core/engines';

function closeAndRun(fn: () => void | Promise<void>) {
  useCommandPaletteStore.getState().close();
  setTimeout(fn, 0);
}

function resolveEngine(connectionId: string): DatabaseEngineId {
  const ac = useConnectionStore.getState().activeConnections
    .find((c) => c.id === connectionId);
  return (ac?.connection.engine || 'mssql') as DatabaseEngineId;
}

export const ddlCommands: CommandDefinition[] = [
  {
    id: 'ddl:create-table',
    label: 'Create Table',
    category: 'script',
    icon: Table,
    keywords: ['ddl', 'create table', 'new table', 'script', 'template'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Create Table',
      );
      if (!ctx?.database) return;
      const engine = resolveEngine(ctx.connectionId);
      handleScriptNewTable(createContextFromDatabaseNode(ctx.connectionId, ctx.database, engine));
    }),
  },
  {
    id: 'ddl:create-view',
    label: 'Create View',
    category: 'script',
    icon: Eye,
    keywords: ['ddl', 'create view', 'new view', 'script', 'template'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Create View',
      );
      if (!ctx?.database) return;
      const engine = resolveEngine(ctx.connectionId);
      handleScriptNewView(createContextFromDatabaseNode(ctx.connectionId, ctx.database, engine));
    }),
  },
  {
    id: 'ddl:create-procedure',
    label: 'Create Procedure',
    category: 'script',
    icon: Cog,
    keywords: ['ddl', 'create procedure', 'stored procedure', 'new procedure', 'sproc', 'script', 'template'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Create Procedure',
      );
      if (!ctx?.database) return;
      const engine = resolveEngine(ctx.connectionId);
      handleScriptNewProcedure(createContextFromDatabaseNode(ctx.connectionId, ctx.database, engine));
    }),
  },
  {
    id: 'ddl:create-function',
    label: 'Create Function',
    category: 'script',
    icon: Braces,
    keywords: ['ddl', 'create function', 'new function', 'udf', 'script', 'template'],
    needsContext: 'database',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: true }, 'Create Function',
      );
      if (!ctx?.database) return;
      const engine = resolveEngine(ctx.connectionId);
      handleScriptNewFunction(createContextFromDatabaseNode(ctx.connectionId, ctx.database, engine));
    }),
  },
  {
    id: 'ddl:create-database',
    label: 'Create Database',
    category: 'script',
    icon: Database,
    keywords: ['ddl', 'create database', 'new database', 'script', 'template'],
    needsContext: 'connection',
    execute: () => closeAndRun(async () => {
      const ctx = await useContextSelectionStore.getState().requestContext(
        { requireConnection: true, requireDatabase: false }, 'Create Database',
      );
      if (!ctx) return;
      handleScriptNewDatabase(ctx.connectionId);
    }),
  },
];
